import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Home, Package, Clock, Truck } from 'lucide-react';
import axios from 'axios';
import { formatPrice, getDeliveryTimeframe } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { trackConversion } from '@/utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ThankYou() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderId && token) {
      fetchOrder();
    }
  }, [orderId, token]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
      
      // Track conversion in Google Ads
      if (response.data && response.data.total) {
        trackConversion('PURCHASE', response.data.total, 'INR');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-20">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle2 className="w-32 h-32 mx-auto mb-8 text-accent" />
          
          <h1 className="text-5xl md:text-6xl font-black uppercase mb-6">
            THANK YOU!
          </h1>
          
          <p className="text-xl text-muted-foreground mb-4">
            Your order has been received successfully.
          </p>
          
          <div className="bg-secondary border border-border rounded-none p-8 mb-8">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">
              Order ID
            </p>
            <p className="mono text-2xl font-black" data-testid="thankyou-order-id">
              {orderId}
            </p>
          </div>

          <div className="space-y-4 text-left bg-white border border-border rounded-none p-8 mb-8">
            <h2 className="text-2xl font-black uppercase mb-4">WHAT'S NEXT?</h2>
            <div className="space-y-3 text-muted-foreground">
              <p className="flex items-start gap-3">
                <span className="text-accent font-bold">1.</span>
                <span>We will verify your payment within 24 hours</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-accent font-bold">2.</span>
                <span>You will receive an order confirmation email at your registered email address</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-accent font-bold">3.</span>
                <span>Our team will contact you for delivery scheduling</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-accent font-bold">4.</span>
                <span>Each product includes a detailed installation manual</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-accent font-bold">5.</span>
                <span>Track your order status in your profile</span>
              </p>
            </div>
          </div>

          {/* Delivery Timeframe - Show only for this specific order */}
          <div className="bg-accent border-2 border-accent rounded-none p-4 sm:p-8 mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-6">
              <Truck className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
              <h3 className="text-lg sm:text-2xl font-black uppercase text-primary text-center">YOUR DELIVERY TIMEFRAME</h3>
            </div>
            
            {order ? (
              <div className="text-center">
                <p className="text-base text-foreground mb-3 font-semibold">
                  Order Value: <span className="mono font-black text-2xl">{formatPrice(order.subtotal)}</span>
                </p>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Clock className="w-8 h-8 text-foreground" />
                  <p className="text-5xl font-black text-foreground mono">
                    {getDeliveryTimeframe(order.subtotal)}
                  </p>
                </div>
                <p className="text-lg text-primary font-bold mb-4">Expected Delivery Time</p>
                
                <div className="mt-6 pt-4 border-t border-primary/30">
                  <p className="text-sm text-foreground italic mb-2">
                    * Subject to certain unavoidable conditions. Force majeure clause applies.
                  </p>
                  <p className="text-base font-bold text-foreground mt-3">
                    ✨ Good News: Delivery timeline may be reduced if items are in stock!
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-foreground">
                <p className="text-lg mb-2 font-semibold">Loading order details...</p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" data-testid="goto-home-button">
              <Button className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground rounded-none uppercase tracking-wider font-bold px-12 py-6">
                <Home className="w-5 h-5 mr-2" />
                GO TO HOME
              </Button>
            </Link>
            <Link to="/profile" data-testid="view-orders-button">
              <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold px-12 py-6">
                <Package className="w-5 h-5 mr-2" />
                VIEW MY ORDERS
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
